

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  applyActionCode,
  checkActionCode,
  ActionCodeInfo,
  sendEmailVerification,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { getApiBaseUrl } from './utils';

// Helper function to get the correct verification redirect URL
const getVerificationRedirectUrl = (): string => {
  const baseUrl = window.location.origin;
  
  if (baseUrl.includes('localhost')) {
    return `${baseUrl}/email-verified`;
  } else if (baseUrl.includes('finanzly-develop.vercel.app')) {
    return 'https://finanzly-develop.vercel.app/email-verified';
  } else if (baseUrl.includes('finanzly.es')) {
    return 'https://finanzly.es/email-verified';
  } else {
    return `${baseUrl}/email-verified`;
  }
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Providers for OAuth
export const googleProvider = new GoogleAuthProvider();
export const microsoftProvider = new OAuthProvider('microsoft.com');
export const appleProvider = new OAuthProvider('apple.com');

// Backend API URL
const API_BASE = `${getApiBaseUrl()}/api`;

// Auth functions using backend
export const signInWithEmail = async (email: string, password: string) => {
  try {
    const response = await fetch(`${API_BASE}/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to sign in');
    }

    // Sign in with the custom token
    const userCredential = await signInWithCustomToken(auth, data.customToken);
    return userCredential;
  } catch (error) {
    throw error;
  }
};

export const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
  try {
    console.log('Starting signup process for:', email);
    
    // 1. Call backend to create user
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, displayName }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to sign up');
    }

    console.log('User created successfully in backend:', data);

    // 2. If email verification is required, send verification email
    if (data.requiresEmailVerification) {
      console.log('Email verification required, attempting to send verification email...');
      
      try {
        // Add a small delay to ensure user is created in Firebase Auth
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Sign in to get the user object
        console.log('Attempting to sign in user to send verification email...');
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('User signed in successfully:', userCredential.user.uid);
        
        // Send email verification using Firebase Auth
        if (!userCredential.user.emailVerified) {
          const redirectUrl = getVerificationRedirectUrl();
          
          console.log('Sending verification email with redirect URL:', redirectUrl);
          await sendEmailVerification(userCredential.user, {
            url: redirectUrl,
            handleCodeInApp: false
          });
          
          console.log('Verification email sent successfully via Firebase Auth');
        } else {
          console.log('User email already verified, skipping email send');
        }
        
        // Sign out immediately after sending verification
        await signOut(auth);
        console.log('User signed out after sending verification email');
      } catch (firebaseError: any) {
        console.error('Error sending verification email:', firebaseError);
        console.error('Firebase error details:', {
          code: firebaseError.code,
          message: firebaseError.message
        });
        
        // If Firebase Auth fails, try alternative approach with retry
        try {
          console.log('Retrying email verification after delay...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const retryCredential = await signInWithEmailAndPassword(auth, email, password);
          console.log('Retry sign in successful:', retryCredential.user.uid);
          
          if (!retryCredential.user.emailVerified) {
            const redirectUrl = getVerificationRedirectUrl();
            
            await sendEmailVerification(retryCredential.user, {
              url: redirectUrl,
              handleCodeInApp: false
            });
            console.log('Verification email sent on retry successfully');
          }
          await signOut(auth);
        } catch (retryError) {
          console.error('Failed to send verification email on retry:', retryError);
          // Continue anyway - user is created, they can request verification later
        }
      }
    } else {
      console.log('Email verification not required for this user');
    }

    // Return the response from backend
    return data;
  } catch (error) {
    console.error('Signup process failed:', error);
    throw error;
  }
};

// OAuth providers still work on frontend
export const signInWithGoogle = () => {
  return signInWithPopup(auth, googleProvider);
};

export const signInWithMicrosoft = () => {
  return signInWithPopup(auth, microsoftProvider);
};

export const signInWithApple = () => {
  return signInWithPopup(auth, appleProvider);
};

export const logout = () => {
  return signOut(auth);
};

// Function to check if user has password authentication (not OAuth)
export const userHasPasswordAuth = (user: User | null): boolean => {
  if (!user) return false;
  
  // Check if the user has any password provider
  const hasPasswordProvider = user.providerData.some(provider => 
    provider.providerId === 'password'
  );
  
  return hasPasswordProvider;
};

// Function to change password
export const updatePassword = async (currentPassword: string, newPassword: string) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user is currently signed in');
    }

    const idToken = await user.getIdToken();
    
    const response = await fetch(`${API_BASE}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ 
        currentPassword, 
        newPassword 
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Function to verify token with backend
export const verifyTokenWithBackend = async (idToken: string) => {
  try {
    const response = await fetch(`${API_BASE}/auth/verify-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to verify token');
    }

    return data.user;
  } catch (error) {
    throw error;
  }
};

// Function to get sign-in methods for an email (backend implementation)
export const getSignInMethodsForEmail = async (email: string) => {
  try {
    const response = await fetch(`${API_BASE}/auth/signin-methods?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to get sign-in methods');
    }

    return data.methods || [];
  } catch (error) {
    return [];
  }
};

// Email verification functions using Firebase Auth
export const verifyEmailWithActionCode = async (actionCode: string): Promise<void> => {
  try {
    // First check if the action code is valid
    const info: ActionCodeInfo = await checkActionCode(auth, actionCode);
    
    // Apply the action code to verify the email
    await applyActionCode(auth, actionCode);
    
    return;
  } catch (error) {
    console.error('Firebase email verification error:', error);
    throw error;
  }
};

// Fallback for custom verification (legacy support)
export const verifyEmail = async (token: string) => {
  try {
    const response = await fetch(`${API_BASE}/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to verify email');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export const resendVerificationEmail = async (email: string, password: string) => {
  try {
    // 1. Check with backend that user exists and needs verification
    const response = await fetch(`${API_BASE}/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to resend verification email');
    }

    // 2. If verification is required, sign in and send email via Firebase
    if (data.requiresEmailVerification) {
      try {
        // Sign in to get the user object
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // Send email verification using Firebase Auth
        if (!userCredential.user.emailVerified) {
          const redirectUrl = getVerificationRedirectUrl();
          
          await sendEmailVerification(userCredential.user, {
            url: redirectUrl,
            handleCodeInApp: false
          });
          
          console.log('Verification email resent via Firebase Auth to redirect to:', redirectUrl);
        }
        
        // Sign out immediately after sending verification
        await signOut(auth);
      } catch (firebaseError: any) {
        console.error('Error resending verification email:', firebaseError);
        
        // Provide more specific error message
        if (firebaseError.code === 'auth/too-many-requests') {
          throw new Error('Too many verification email requests. Please wait before trying again.');
        } else if (firebaseError.code === 'auth/invalid-credential') {
          throw new Error('Invalid password. Please check your password and try again.');
        } else {
          throw new Error('Failed to send verification email. Please try again later.');
        }
      }
    }

    return { success: true, message: 'Verification email sent successfully' };
  } catch (error) {
    throw error;
  }
};

// Simple function to send verification email to logged-in user
export const sendVerificationEmailToUser = async (user: User) => {
  try {
    if (!user.emailVerified) {
      const redirectUrl = getVerificationRedirectUrl();
      
      await sendEmailVerification(user, {
        url: redirectUrl,
        handleCodeInApp: false
      });
      console.log('Verification email sent via Firebase Auth to redirect to:', redirectUrl);
    }
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

export const checkEmailVerificationWithCredentials = async (email: string, password: string): Promise<boolean> => {
  try {
    
    // Try to sign in with the provided credentials
    const response = await fetch(`${API_BASE}/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    if (response.ok) {
      // Successful login means email is verified
      
      // Sign in to Firebase with the custom token
      const userCredential = await signInWithCustomToken(auth, data.customToken);
      
      // Check the verification status from the user object
      const isVerified = userCredential.user.emailVerified;
      
      return isVerified;
    } else {
      // Check the specific error
      if (data.code === 'email-not-verified') {
        return false;
      } else if (data.code === 'wrong-password' || data.code === 'invalid-credential') {
        return false; // We can't verify without correct password
      } else {
        return false;
      }
    }
  } catch (error) {
    console.error('âŒ Error checking email verification with credentials:', error);
    return false;
  }
};

export const checkEmailVerificationStatus = async (email?: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    
    // If we have a current user, use the standard flow
    if (currentUser) {
      
      // Force reload user data from Firebase
      await currentUser.reload();
      
      // Get the latest verification status
      const isVerified = currentUser.emailVerified;
      
      if (isVerified) {
        // If verified, also refresh the token to ensure backend is updated
        const idToken = await currentUser.getIdToken(true);
        await verifyTokenWithBackend(idToken);
      }
      
      return isVerified;
    }
    
    // If no current user and email provided, we can't check without password
    if (email) {
      return false;
    }
    
    return false;
  } catch (error) {
    console.error('âŒ Error checking email verification status:', error);
    return false;
  }
};


